// On a client's record (VERA AEC): the owner portal. An owner makes a private link for this client; the client opens it
// without an account and sees their projects, milestones, what is shared with them, and the decisions waiting for them.
// The link is shown once, when it is made (only its fingerprint is kept), and can be revoked at any time.
import { useCallback, useEffect, useState } from "react";
import { crm, type CrmRecord, type PortalLinkInfo } from "../lib/crm";
import { T } from "../lib/i18n";
import { Icon, useStudio } from "./ui";

export function OwnerPortalCard({ client }: { client: CrmRecord }) {
  const { getToken, toast } = useStudio();
  const [links, setLinks] = useState<PortalLinkInfo[] | null>(null);
  const [fresh, setFresh] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const r = await crm.portalLinks(getToken);
    setLinks(r.links.filter((l) => l.clientId === client.id && !l.revoked && l.expires > Date.now()));
  }, [getToken, client.id]);
  useEffect(() => { void load().catch(() => setLinks([])); }, [load]);

  async function make() {
    setBusy(true);
    try { const r = await crm.makePortalLink(getToken, client.id); setFresh(r.url); await load(); } catch (e) { toast(e instanceof Error ? e.message : T("Something went wrong.")); } finally { setBusy(false); }
  }
  async function revoke(id: string) {
    if (!confirm(T("Switch off this portal link? It stops working at once."))) return;
    setBusy(true);
    try { await crm.revokePortalLink(getToken, id); if (fresh) setFresh(null); await load(); toast(T("Switched off.")); } catch (e) { toast(e instanceof Error ? e.message : T("Something went wrong.")); } finally { setBusy(false); }
  }

  return (
    <div className="wide staffcard">
      <dt>{T("Owner portal")}</dt>
      <dd className="stack" style={{ gap: 8 }}>
        <span className="faint small">{T("A private page for this client: their projects, milestones, the reports, documents and designs you share with them, and the decisions waiting for them. No account needed.")}</span>
        {fresh && (
          <div className="notice calm stack" style={{ gap: 6 }}>
            <span>{T("Here is the link. Copy it now: it is shown only once.")}</span>
            <code className="small" style={{ wordBreak: "break-all" }}>{fresh}</code>
            <div className="row" style={{ gap: 6 }}>
              <button className="btn sm" onClick={() => { void navigator.clipboard?.writeText(fresh); toast(T("Copied")); }}>{T("Copy")}</button>
              <a className="btn sm" href={fresh} target="_blank" rel="noreferrer noopener">{T("Open")}</a>
            </div>
          </div>
        )}
        {links && links.length > 0 && (
          <div className="list">{links.map((l) => (
            <div className="li" key={l.id}>
              <div className="grow small">{T("Active link")} · {T("made by")} {l.createdBy} · {new Date(l.created).toLocaleDateString()}</div>
              <button className="btn sm" disabled={busy} onClick={() => void revoke(l.id)}>{T("Switch off")}</button>
            </div>))}
          </div>
        )}
        <div><button className="btn" disabled={busy} onClick={() => void make()}><Icon name="lock" size={14} /> {links?.length ? T("Make a new link") : T("Make a portal link")}</button></div>
      </dd>
    </div>
  );
}
